import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/utils";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import { IconButton } from "@/components/ui/icon-button";
import { IconsMap } from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import { InfoCircle } from "@dreamhub/designsystem/icons";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";

interface DetailFieldRowProps {
  label?: string;
  icon?: string;
  helpText?: string;
  hasError?: boolean;
  // A pair shares one value cell, so the caller lays out both halves inside it.
  paired?: boolean;
  // The three growth shapes the value cell has to accommodate. They are passed
  // as flags rather than derived from a field type here: the row shell has no
  // business knowing the field-type vocabulary, and a pair takes its growth
  // from whichever half grows.
  isTextarea?: boolean;
  isMultiInput?: boolean;
  growsWithContent?: boolean;
  children: React.ReactNode;
}

/**
 * One label/value row of a detail-field grid. Renders as `contents` so the
 * label and value cells land directly in the parent grid's columns.
 */
export const DetailFieldRow = ({
  label,
  icon,
  helpText,
  hasError,
  paired,
  isTextarea = false,
  isMultiInput = false,
  growsWithContent = false,
  children
}: DetailFieldRowProps) => {
  const { t } = useTranslation();

  return (
    <div className="group contents">
      <div
        className={cn(
          "flex gap-2 pr-4 h-9",
          isTextarea || growsWithContent
            ? "items-start pt-2 overflow-visible"
            : "overflow-hidden",
          hasError || isMultiInput || isTextarea || growsWithContent
            ? "items-start pt-2"
            : "items-center"
        )}
      >
        {icon && IconsMap?.[icon] && (
          <IconContainer size="sm" color="secondary">
            {IconsMap[icon]}
          </IconContainer>
        )}
        <div className="flex items-center gap-1 min-w-0 flex-1">
          {label !== "" && (
            <Text
              variant="bodySecondary"
              className="truncate"
              hasTruncatedTooltip
            >
              {label}
            </Text>
          )}
          {helpText && (
            <Tooltip>
              <TooltipTrigger asChild>
                {/* Focusable and named: a bare div keeps the help text off the
                  tab order entirely, so keyboard users never reach it. */}
                <IconButton
                  type="button"
                  // Generic, like HelpTextTooltip's own button: Radix puts the tooltip id
                  // on aria-describedby, so naming the button with the same text
                  // makes assistive tech announce the help twice.
                  aria-label={t("help")}
                  size="xs"
                  variant="iconNoBg"
                  iconColor="secondary"
                >
                  <InfoCircle />
                </IconButton>
              </TooltipTrigger>
              <TooltipContent maxWidth={13}>
                <Text variant="smBody">{helpText}</Text>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
      <div
        className={cn(
          // min-w-0 caps this grid item's own contribution to the shared 1fr
          // value column. Without it, a child that reports its full max-content
          // width on hover (TextWithHoverAction's flex-initial swap, so it can
          // yield space to the revealed action) forces the whole column wider —
          // every row using it visibly jumps, not just the hovered one.
          "flex break-words w-full min-w-0",
          paired && "gap-2",
          isTextarea
            ? "h-fit items-start min-h-9 overflow-visible"
            : isMultiInput
              ? "h-fit items-start min-h-[2rem] overflow-visible"
              : growsWithContent
                ? "h-fit items-start min-h-9 overflow-visible"
                : hasError
                  ? "h-16 items-start overflow-hidden"
                  : "h-9 items-center overflow-visible"
        )}
      >
        {children}
      </div>
    </div>
  );
};
